/*
  Landing page product cards (sections/invisibra-lp-bestsellers.liquid).

  Each card is an <ib-lp-card> wrapping the theme's own <product-form>, so adding to the basket
  goes through assets/main.js (ProductForm): it posts to /cart/add.js, refreshes the header count
  and fires the add-to-basket toast from layout/theme.liquid. This file only keeps the card's
  hidden "id" input in step with the size and colour chips, and swaps the price, the colour label
  and the free-delivery nudge for the selected variant.

  Everything it shows is rendered by Liquid into the card's JSON block (prices and nudges already
  formatted, so there's no money formatting here). The option positions come from the product, so
  a product with the colour option first still works.
*/
if (!customElements.get('ib-lp-card')) {
  class IbLpCard extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-ib-lp-variants]');
      if (!dataEl) return;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      if (!this.data || !Array.isArray(this.data.variants)) return;

      this.idInput = this.querySelector('[data-ib-lp-id]');
      this.priceEl = this.querySelector('[data-ib-lp-price]');
      this.colourEl = this.querySelector('[data-ib-lp-colour]');
      this.nudgeEl = this.querySelector('[data-ib-lp-nudge]');
      this.button = this.querySelector('[name="add"]');
      this.sizeInputs = Array.from(this.querySelectorAll('[data-ib-lp-size]'));
      this.colourInputs = Array.from(this.querySelectorAll('[data-ib-lp-colour-input]'));

      this.addEventListener('change', (evt) => {
        if (evt.target.matches('[data-ib-lp-size], [data-ib-lp-colour-input]')) this.update();
      });

      this.update();
    }

    /** @returns {string|null} The checked value of a radio group. */
    static checked(inputs) {
      const hit = inputs.find((input) => input.checked);
      return hit ? hit.value : null;
    }

    /**
     * Find the variant for a size and colour. A product may have only one of the two options, in
     * which case that position is ignored.
     * @returns {object|undefined} The matching variant from the card's JSON.
     */
    variantFor(size, colour) {
      const { sizeIndex, colourIndex, variants } = this.data;
      return variants.find((variant) => {
        if (sizeIndex > -1 && variant.options[sizeIndex] !== size) return false;
        if (colourIndex > -1 && variant.options[colourIndex] !== colour) return false;
        return true;
      });
    }

    update() {
      const colour = IbLpCard.checked(this.colourInputs);
      let size = IbLpCard.checked(this.sizeInputs);

      // Grey out the sizes this colour doesn't come in, or has sold out of.
      this.sizeInputs.forEach((input) => {
        const variant = this.variantFor(input.value, colour);
        input.disabled = !(variant && variant.available);
      });

      // If the chosen size isn't sellable in this colour, move to the first one that is, so the
      // form always holds a variant the customer can actually buy.
      let variant = this.variantFor(size, colour);
      if (!variant || !variant.available) {
        const fallback = this.sizeInputs.find((input) => !input.disabled);
        if (fallback) {
          fallback.checked = true;
          size = fallback.value;
          variant = this.variantFor(size, colour);
        }
      }

      if (this.colourEl && colour) this.colourEl.textContent = colour;

      if (!variant) {
        this.setSoldOut();
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      if (this.priceEl) this.priceEl.innerHTML = variant.priceHtml;

      if (this.nudgeEl) {
        this.nudgeEl.textContent = variant.nudge || '';
        this.nudgeEl.hidden = !variant.nudge;
      }

      if (this.button) {
        this.button.disabled = !variant.available;
        this.button.textContent = variant.available ? this.data.addText : this.data.soldOutText;
      }
    }

    setSoldOut() {
      if (!this.button) return;
      this.button.disabled = true;
      this.button.textContent = this.data.soldOutText;
      if (this.nudgeEl) this.nudgeEl.hidden = true;
    }
  }

  customElements.define('ib-lp-card', IbLpCard);
}
